import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import {
  MENU_TRAINING_CHECKLIST_PATH,
  MENU_TRAINING_PILLAR_PATH,
  MENU_TRAINING_TEMPLATE_PATH,
} from "../lib/contentClusters";
import "../styles/homepage-redesign.css";

const startFreeHref = "https://app.speakyourmenu.com/signup";
const canonicalUrl = `https://speakyourmenu.com${MENU_TRAINING_PILLAR_PATH}`;
const checklistUrl = `https://speakyourmenu.com${MENU_TRAINING_CHECKLIST_PATH}`;
const pageTitle = "Restaurant Menu Training Program | Speak Your Menu";
const pageDescription = "A practical restaurant menu training program for operators who need faster onboarding, stronger retention, and more consistent guest-facing menu knowledge.";

const jumpLinks = [
  { href: "#what-it-is", label: "What it is" },
  { href: "#why-it-fails", label: "Why training breaks" },
  { href: "#system", label: "The system" },
  { href: "#week-plan", label: "7-day rollout" },
  { href: "#mistakes", label: "Common mistakes" },
  { href: "#faq", label: "FAQ" },
];

const keyTakeaways = [
  "Menu training works better when staff practice in short loops instead of one-time memorization sessions.",
  "The highest-impact program combines accurate menu data, repetition, recall, and manager visibility.",
  "A strong system should improve onboarding speed, service consistency, allergen confidence, and upsell readiness together.",
];

const systemSteps = [
  {
    title: "Build one source of truth",
    body: "Start with your real menu, modifiers, allergens, and guest-facing language so every lesson reinforces the same standard.",
  },
  {
    title: "Teach in short, repeatable sessions",
    body: "Staff retain more when practice happens in small daily loops instead of one long pre-shift download.",
  },
  {
    title: "Reinforce under pressure",
    body: "Repetition matters most after the first week, when service pace starts to crowd out recall and confidence.",
  },
  {
    title: "Give managers visibility",
    body: "If you cannot see weak spots, you cannot coach them early. Progress data is what makes the program sustainable.",
  },
];

const weekPlan = [
  "Day 1: Upload the menu and define the guest-facing standard for dishes, allergens, substitutions, and pairings.",
  "Day 2: Roll out the first lesson block to new hires and current staff with a clear completion expectation.",
  "Day 3: Review early weak spots and align managers on the phrases or dish details that need reinforcing.",
  "Day 4: Add a short pre-shift reinforcement loop tied to the same menu sections staff practiced digitally.",
  "Day 5: Check completions and accuracy so coaching starts before the weekend rush exposes gaps on the floor.",
  "Day 6: Reinforce high-risk areas such as modifiers, allergens, and items that staff hesitate to describe.",
  "Day 7: Review what held, what slipped, and what needs to become part of the weekly operating cadence.",
];

const mistakes = [
  "Treating menu training like a one-time onboarding task instead of an operating system that needs reinforcement.",
  "Relying on shadow shifts alone, which creates uneven knowledge transfer depending on who happens to train each new hire.",
  "Teaching dish facts without practicing the language staff actually need at the table.",
  "Skipping manager follow-through, which removes accountability and lets confidence gaps stay hidden until service.",
];

const faqs = [
  {
    question: "How long should restaurant menu training take?",
    answer: "The initial setup can happen in a few days, but retention improves when training continues in short cycles across the first few weeks instead of ending after orientation.",
  },
  {
    question: "What should a menu training program include?",
    answer: "At minimum, it should include accurate menu data, guest-ready language, allergens and substitutions, spaced reinforcement, and a way for managers to see where knowledge is slipping.",
  },
  {
    question: "Why do staff still freeze even after pre-shift training?",
    answer: "Because one-time exposure is rarely enough. Staff need repeated retrieval and realistic phrasing practice if you want confidence to survive a busy shift.",
  },
];

export default function MenuTrainingPage() {
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://speakyourmenu.com/" },
      { "@type": "ListItem", position: 2, name: "Restaurant Menu Training Program", item: canonicalUrl },
    ],
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Restaurant Menu Training Program: A Practical System for Faster Onboarding and Stronger Retention",
    description: pageDescription,
    author: { "@type": "Organization", name: "Speak Your Menu" },
    publisher: { "@type": "Organization", name: "Speak Your Menu" },
    mainEntityOfPage: canonicalUrl,
    dateModified: new Date().toISOString(),
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="Speak Your Menu" />
        <meta property="og:image" content="https://speakyourmenu.com/SpeakYourMenu_Logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content="https://speakyourmenu.com/SpeakYourMenu_Logo.png" />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>
      <Nav />
      <main id="main-content" tabIndex="-1" className="homepage-redesign resource-page-shell">
        <section className="homepage-section homepage-hero-section resource-hero-section">
          <div className="homepage-hero-bg" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-left" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-right" aria-hidden="true" />
          <div className="homepage-shell">
            <div className="resource-hero-copy">
              <span className="homepage-label-chip">Menu Training Systems</span>
              <h1 className="homepage-hero-title resource-hero-title">Restaurant menu training that holds up during real service.</h1>
              <p className="homepage-hero-subhead resource-hero-subhead">
                This guide shows restaurant operators how to build a menu training program that improves onboarding speed, knowledge retention, and guest-facing confidence without piling more manual work onto managers.
              </p>
              <div className="homepage-hero-actions resource-hero-actions">
                <a href={startFreeHref} className="homepage-primary-cta">Start free</a>
                <Link to={MENU_TRAINING_CHECKLIST_PATH} className="resource-secondary-link">Open the checklist</Link>
              </div>
              <div className="resource-jump-card homepage-surface-card">
                <p className="resource-jump-kicker">In this guide</p>
                <div className="resource-jump-links" aria-label="Jump to sections">
                  {jumpLinks.map((link) => (
                    <a key={link.href} href={link.href}>{link.label}</a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="homepage-section">
          <div className="homepage-shell resource-grid-two">
            <article className="homepage-surface-card resource-card" id="what-it-is">
              <span className="homepage-label-chip">Key takeaways</span>
              <h2 className="homepage-section-title resource-section-title">What a modern menu training program should do</h2>
              <ul className="resource-bullet-list">
                {keyTakeaways.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
            <aside className="homepage-surface-card resource-card resource-card-accent">
              <span className="homepage-label-chip">Related resource</span>
              <h2 className="homepage-card-title resource-aside-title">Need the shorter version?</h2>
              <p className="homepage-card-copy">Use the checklist as the operating baseline, then come back here when you want the full reasoning and rollout guidance.</p>
              <Link to={MENU_TRAINING_CHECKLIST_PATH} className="homepage-primary-cta resource-inline-cta">View checklist</Link>
            </aside>
          </div>
        </section>

        <section className="homepage-section homepage-section-alt" id="why-it-fails">
          <div className="homepage-shell resource-copy-shell">
            <span className="homepage-label-chip">Why training breaks</span>
            <h2 className="homepage-section-title resource-section-title">The usual problem is not effort. It is system design.</h2>
            <p className="homepage-section-body resource-section-body">
              Most restaurants do not have a menu training problem because staff are lazy or managers do not care. They have a system problem: information is compressed into orientation, shadowing carries too much weight, and nobody has a durable reinforcement loop once service gets busy.
            </p>
            <p className="homepage-section-body resource-section-body">
              That is why knowledge fades unevenly. Some employees retain enough to sound confident, others hesitate on allergens, pairings, or dish descriptions, and guests receive inconsistent answers depending on the shift. A reliable training program fixes that by replacing one-time exposure with repeatable practice and visible standards.
            </p>
          </div>
        </section>

        <section className="homepage-section" id="system">
          <div className="homepage-shell">
            <span className="homepage-label-chip">The system</span>
            <h2 className="homepage-section-title resource-section-title">A practical four-part system for restaurant menu training</h2>
            <div className="resource-step-grid">
              {systemSteps.map((step, index) => (
                <article key={step.title} className="homepage-surface-card resource-step-card">
                  <div className="homepage-step-number">0{index + 1}</div>
                  <h3 className="homepage-card-title">{step.title}</h3>
                  <p className="homepage-card-copy">{step.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="homepage-section homepage-section-alt" id="week-plan">
          <div className="homepage-shell resource-copy-shell">
            <span className="homepage-label-chip">7-day rollout</span>
            <h2 className="homepage-section-title resource-section-title">How to implement the system in one week</h2>
            <ol className="resource-numbered-list">
              {weekPlan.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ol>
          </div>
        </section>

        <section className="homepage-section" id="mistakes">
          <div className="homepage-shell resource-grid-two">
            <article className="homepage-surface-card resource-card">
              <span className="homepage-label-chip">Common mistakes</span>
              <h2 className="homepage-section-title resource-section-title">Four mistakes that keep menu training from sticking</h2>
              <ul className="resource-bullet-list">
                {mistakes.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
            <article className="homepage-surface-card resource-card resource-card-contrast">
              <span className="homepage-label-chip">Next action</span>
              <h2 className="homepage-card-title resource-aside-title">Turn this into an operating checklist</h2>
              <p className="homepage-card-copy">If you want the shorter execution layer, use the checklist page to translate the guide into concrete team habits.</p>
              <Link to={MENU_TRAINING_CHECKLIST_PATH} className="resource-secondary-link resource-secondary-link-inline">Go to checklist</Link>
              <Link to={MENU_TRAINING_TEMPLATE_PATH} className="resource-tertiary-link">Or get the free template</Link>
            </article>
          </div>
        </section>

        <section className="homepage-section homepage-section-alt" id="faq">
          <div className="homepage-shell resource-copy-shell">
            <span className="homepage-label-chip">FAQ</span>
            <h2 className="homepage-section-title resource-section-title">Questions operators ask before rebuilding menu training</h2>
            <div className="resource-faq-stack">
              {faqs.map((item) => (
                <article key={item.question} className="homepage-surface-card resource-faq-card">
                  <h3 className="homepage-card-title">{item.question}</h3>
                  <p className="homepage-card-copy">{item.answer}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="homepage-section">
          <div className="homepage-shell homepage-final-cta-shell resource-final-shell">
            <div>
              <span className="homepage-label-chip">Ready to apply it?</span>
              <h2 className="homepage-section-title resource-section-title">See how Speak Your Menu turns this system into daily training.</h2>
              <p className="homepage-section-body resource-section-body">
                The platform turns your real menu into repeatable lessons, tracks progress, and helps managers coach before gaps show up in service.
              </p>
            </div>
            <div className="resource-final-actions">
              <a href={startFreeHref} className="homepage-primary-cta">Start free</a>
              <Link to={MENU_TRAINING_TEMPLATE_PATH} className="resource-secondary-link">Get template</Link>
              <Link to={MENU_TRAINING_CHECKLIST_PATH} className="resource-secondary-link">Open checklist</Link>
              <a href={checklistUrl} className="resource-tertiary-link">Share the checklist URL</a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}